import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Play, Pause, ChevronDown, ChevronUp } from 'lucide-react';

const ACTS = [
  {
    id: 1,
    title: 'THE PROBLEM',
    duration: '10m',
    description: 'Every discipline inherits assumptions about how world becomes knowable',
    subhead: 'Grad lays out the primitive entities. Genevieve stops the mysticism before it starts.',
    scenes: [
      {
        stage: `[LIGHTS: Single worklight center. Chalkboard upstage covered in equations. GRAD in canvas work shirt, GENEVIEVE in blazer with MLA Handbook sticking out of pocket. A folding table, two mics.]`,
        lines: [
          { speaker: 'GENEVIEVE', style: 'sharp / MLA-citing', text: 'Every discipline inherits assumptions about how world becomes knowable. So let\'s name them.' },
          { speaker: 'GRAD FAWKES', style: 'gruff / framework', text: 'Right. You can\'t build anything without a substrate. Mycelium doesn\'t grow in air. It needs wood. Ideas don\'t resolve in void.' },
        ],
        projection: {
          title: 'Primitive Entities — The Stack',
          formula: 'O → Φ → A₁₂ + Ω / Σ → C'
        },
        moreLines: [
          { speaker: 'GRAD FAWKES', style: 'gruff / framework', text: 'We got six primitives. Observer O — not the eyeball, the locating function. Field Φ — the plenum, everything that could be said. Attractor Set A-twelve — the twelve ruts we keep falling into. Overflow Ω — the thirteenth thing that won\'t fit. Substrate Σ — the body, the budget, the building. And Collapse C —' },
          { speaker: 'GRAD FAWKES', style: 'gruff / framework', text: 'Every utterance is a collapse. You take this infinite maybe — and you force it to land somewhere. That landing is never neutral. It costs.' },
        ],
        critique: {
          speaker: 'GENEVIEVE',
          text: 'You say quantum, reviewers hear mysticism. If you write P(phi) as superposition without an operational definition, you will be desk-rejected by both physicists and sociologists. They will be united, for once. Spell it out as constraint, not magic. The field Φ is discursive potential. Collapse C is what your substrate lets you afford to say. That\'s materialist. Keep it materialist.'
        }
      }
    ]
  },
  {
    id: 2,
    title: 'THE SPIRAL MECHANISM',
    duration: '15m',
    description: 'How collapse generates motion through twelve attractors',
    subhead: 'The spiral isn\'t metaphor. It\'s the geometry of how truth-seeking systems fail forward.',
    scenes: [
      {
        stage: `[PROJECTION shifts to animated spiral. Twelve points glow in sequence.]`,
        lines: [
          { speaker: 'GRAD FAWKES', style: 'gruff / framework', text: 'Twelve attractors. Not random. Each one is a rut worn by centuries of collapse. Philosophy landed here, science there, religion over there.' },
          { speaker: 'GENEVIEVE', style: 'sharp / MLA-citing', text: 'You\'re describing epistemic path dependency.' },
          { speaker: 'GRAD FAWKES', style: 'gruff / framework', text: 'I\'m describing grooves. When you collapse the field, you don\'t land random. You land where the wheel\'s already worn.' },
        ],
        moreLines: [
          { speaker: 'GRAD FAWKES', style: 'gruff / framework', text: 'But here\'s the trick — the thirteenth position. Omega. The overflow. It\'s not a thirteenth groove. It\'s what happens when you refuse to land.' },
          { speaker: 'GENEVIEVE', style: 'sharp / MLA-citing', text: 'Apophatic epistemology. The via negativa.' },
          { speaker: 'GRAD FAWKES', style: 'gruff / framework', text: 'Call it whatever gets it past peer review. Point is: sometimes the most honest move is to not-land. To hold the superposition.' },
        ]
      }
    ]
  },
  {
    id: 3,
    title: 'THE 12+1 ARCHITECTURE',
    duration: '10m',
    description: 'Mapping the attractor set to existing knowledge systems',
    subhead: 'Where does your discipline land? Where does it refuse to?',
    scenes: [
      {
        stage: `[Chalkboard lights up. GRAD draws twelve points in a circle, labels each.]`,
        lines: [
          { speaker: 'GRAD FAWKES', style: 'gruff / framework', text: 'Empiricism lands at one. Rationalism at two. Pragmatism at three. Keep going around the wheel — phenomenology, structuralism, post-structuralism, systems theory, critical theory, hermeneutics, naturalism, idealism, skepticism.' },
          { speaker: 'GENEVIEVE', style: 'sharp / MLA-citing', text: 'And the thirteenth?' },
          { speaker: 'GRAD FAWKES', style: 'gruff / framework', text: 'Silence. The one that says: I could land, but I won\'t. Not yet. The holding pattern.' },
        ],
        projection: {
          title: 'The Wheel of Landing',
          formula: 'A₁₂ = {Emp, Rat, Prag, Phen, Struct, Post, Sys, Crit, Herm, Nat, Ideal, Skep} + Ω'
        }
      }
    ]
  },
  {
    id: 4,
    title: 'UNITY — ECHAD',
    duration: '10m',
    description: 'The integration that doesn\'t flatten',
    subhead: 'Not synthesis. Not compromise. Echad: the one that holds the many without erasing them.',
    scenes: [
      {
        stage: `[Lights shift to warm amber. The spiral projection slows, then stops.]`,
        lines: [
          { speaker: 'GENEVIEVE', style: 'sharp / MLA-citing', text: 'So what\'s the endgame? Twelve perspectives plus silence equals... what?' },
          { speaker: 'GRAD FAWKES', style: 'gruff / framework', text: 'Echad. Hebrew. Usually translated "one." But it\'s not one like a number. It\'s one like a marriage. Two becoming one without either disappearing.' },
          { speaker: 'GENEVIEVE', style: 'sharp / MLA-citing', text: 'Unity that preserves difference.' },
          { speaker: 'GRAD FAWKES', style: 'gruff / framework', text: 'Truth isn\'t a noun you find. It\'s a verb you bend. You bend it by collapsing the field, knowing you\'re collapsing it, holding the knowledge that you could have landed elsewhere.' },
        ],
        moreLines: [
          { speaker: 'GRAD FAWKES', style: 'gruff / framework', text: 'The thirteenth tone isn\'t silence because nothing can be said. It\'s silence because everything has been said, and you\'re choosing not to add more noise.' },
          { speaker: 'GENEVIEVE', style: 'sharp / MLA-citing', text: 'Negative capability.' },
          { speaker: 'GRAD FAWKES', style: 'gruff / framework', text: 'Keats had it. So did the rabbis. So does anyone who sits with the question longer than the answer requires.' },
        ],
        finale: 'PROJECTION FADES TO: "Truth is a verb you bend."'
      }
    ]
  }
];

export default function TruthIsVerbPage() {
  const [expandedAct, setExpandedAct] = useState(1);
  const [timer, setTimer] = useState(null);

  const toggleAct = (actId) => {
    setExpandedAct(expandedAct === actId ? null : actId);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-stone-100">
      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-stone-800/60 bg-[#0e0e10]/95 backdrop-blur px-4 py-4">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-stone-400 hover:text-stone-200 transition-all">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to MGN1.6</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-xs tracking-[0.2em] uppercase text-amber-300/70">PPC Framework</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto">
          <p className="text-xs tracking-[0.3em] uppercase text-amber-300 mb-4">Philosophical Performance Piece</p>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight leading-[0.9] text-stone-100 mb-6">
            Truth Is a Verb<br />
            <span className="text-amber-300">You Bend</span>
          </h1>
          <p className="text-lg md:text-xl text-stone-400 max-w-2xl mb-8">
            A four-act framework for understanding how knowledge systems collapse infinite possibility 
            into finite claims — and what happens when you refuse to land.
          </p>
          
          {/* Act Navigation */}
          <div className="flex flex-wrap gap-2 mb-12">
            {ACTS.map((act) => (
              <button
                key={act.id}
                onClick={() => toggleAct(act.id)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  expandedAct === act.id 
                    ? 'bg-amber-300 text-[#0a0a0c]' 
                    : 'bg-stone-800 text-stone-300 hover:bg-stone-700'
                }`}
                data-testid={`act-${act.id}-btn`}
              >
                Act {act.id}: {act.title}
                <span className="ml-2 text-xs opacity-70">{act.duration}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Acts Content */}
      <section className="px-4 pb-16">
        <div className="max-w-[1400px] mx-auto space-y-6">
          {ACTS.map((act) => (
            <article 
              key={act.id}
              className={`rounded-xl border transition-all ${
                expandedAct === act.id 
                  ? 'border-amber-300/40 bg-[#121214]' 
                  : 'border-stone-800 bg-[#0e0e10]'
              }`}
              data-testid={`act-${act.id}`}
            >
              {/* Act Header */}
              <button
                onClick={() => toggleAct(act.id)}
                className="w-full p-6 flex items-start justify-between text-left"
              >
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs tracking-[0.2em] uppercase text-amber-300">Act {act.id}</span>
                    <span className="text-xs text-stone-500">{act.duration}</span>
                  </div>
                  <h2 className="text-2xl font-bold text-stone-100 mb-2">{act.title}</h2>
                  <p className="text-stone-400">{act.description}</p>
                  <p className="text-sm text-stone-500 mt-2 italic">{act.subhead}</p>
                </div>
                {expandedAct === act.id ? (
                  <ChevronUp className="w-6 h-6 text-amber-300 shrink-0" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-stone-500 shrink-0" />
                )}
              </button>

              {/* Act Content */}
              {expandedAct === act.id && (
                <div className="px-6 pb-6 space-y-6">
                  {act.scenes.map((scene, idx) => (
                    <div key={idx} className="space-y-4">
                      {/* Stage Direction */}
                      <div className="p-4 rounded-lg bg-[#17171a] border border-stone-800">
                        <p className="text-xs tracking-[0.18em] uppercase text-cyan-300 mb-2">↳ STAGE</p>
                        <p className="text-sm text-stone-400 italic">{scene.stage}</p>
                      </div>

                      {/* Dialogue */}
                      {scene.lines.map((line, lineIdx) => (
                        <div key={lineIdx} className="flex gap-4">
                          <div className="w-10 h-10 rounded-full bg-amber-300/20 flex items-center justify-center shrink-0">
                            <span className="text-amber-300 font-bold text-sm">
                              {line.speaker === 'GENEVIEVE' ? 'Gv' : 'GF'}
                            </span>
                          </div>
                          <div>
                            <p className="text-amber-200 font-semibold">{line.speaker}</p>
                            <p className="text-xs text-stone-500 mb-1">• {line.style}</p>
                            <p className="text-stone-200">{line.text}</p>
                          </div>
                        </div>
                      ))}

                      {/* Projection */}
                      {scene.projection && (
                        <div className="p-4 rounded-lg bg-[#1a1810] border border-amber-300/20">
                          <p className="text-xs tracking-[0.18em] uppercase text-amber-300 mb-2">PROJECTION</p>
                          <p className="text-sm text-amber-200 mb-2">{scene.projection.title}</p>
                          <p className="font-mono text-lg text-amber-100">{scene.projection.formula}</p>
                        </div>
                      )}

                      {/* More Lines */}
                      {scene.moreLines?.map((line, lineIdx) => (
                        <div key={`more-${lineIdx}`} className="flex gap-4">
                          <div className="w-10 h-10 rounded-full bg-amber-300/20 flex items-center justify-center shrink-0">
                            <span className="text-amber-300 font-bold text-sm">
                              {line.speaker === 'GENEVIEVE' ? 'Gv' : 'GF'}
                            </span>
                          </div>
                          <div>
                            <p className="text-amber-200 font-semibold">{line.speaker}</p>
                            <p className="text-xs text-stone-500 mb-1">• {line.style}</p>
                            <p className="text-stone-200">{line.text}</p>
                          </div>
                        </div>
                      ))}

                      {/* Critique */}
                      {scene.critique && (
                        <div className="p-4 rounded-lg bg-[#0f1e1e] border border-cyan-300/20 ml-14">
                          <p className="text-xs tracking-[0.18em] uppercase text-cyan-300 mb-2">{scene.critique.speaker} — LIVE CRITIQUE</p>
                          <p className="text-cyan-50/90">{scene.critique.text}</p>
                        </div>
                      )}

                      {/* Finale */}
                      {scene.finale && (
                        <div className="p-6 rounded-lg bg-gradient-to-r from-amber-300/20 via-amber-200/10 to-transparent border border-amber-300/30 text-center">
                          <p className="text-amber-200 font-semibold text-lg">{scene.finale}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* Footer Quote */}
      <section className="px-4 py-16 border-t border-stone-800">
        <div className="max-w-[1400px] mx-auto text-center">
          <blockquote className="text-3xl md:text-4xl font-black text-amber-300 mb-4">
            "Truth is a verb you bend."
          </blockquote>
          <p className="text-stone-500">
            A PPC Framework Production · Grad Fawkes & Genevieve
          </p>
        </div>
      </section>
    </div>
  );
}
